import { initializeApp } from "firebase/app"
import { initializeAuth, getAuth, browserLocalPersistence } from "firebase/auth"
import { getFirestore } from "firebase/firestore"
import { CustomProvider, initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check"
import { getAnalytics, isSupported } from "firebase/analytics"
import { getFunctions, connectFunctionsEmulator } from "firebase/functions"

const productionConfig = {
  apiKey: "AIzaSyAeCT0f2rr_7uRxdZb_0ZRGeC5xzrVhdrg",
  authDomain: "rops-kanban.firebaseapp.com",
  projectId: "rops-kanban",
  storageBucket: "rops-kanban.firebasestorage.app",
  messagingSenderId: "564388405283",
  appId: "1:564388405283:web:914b41fb0dbabda0481196",
  measurementId: "G-R8N9FV13GJ"
}

const developmentConfig = {
  apiKey: "AIzaSyBFHE7bmnaO0aIbYEwzFEC7JbQurkk-mhY",
  authDomain: "dev-env-rops-kanban.firebaseapp.com",
  projectId: "dev-env-rops-kanban",
  storageBucket: "dev-env-rops-kanban.firebasestorage.app",
  messagingSenderId: "600049542945",
  appId: "1:600049542945:web:cf2415873add58f23fc060",
  measurementId: "G-1T0TV8B4H3"
}

// reCAPTCHA v3 site keys. Public by design -- they ship in the bundle and are
// scoped to the domains registered with reCAPTCHA. The matching *secret* keys
// live only in the Firebase console's App Check registration.
const RECAPTCHA_SITE_KEYS = {
  production: "6LccvpYtAAAAAH4qgvwJVz1-69tmoWvY6eKyWtX2",
  development: "" // dev-env-rops-kanban needs its own reCAPTCHA v3 key
}

const mode = import.meta.env.MODE
const firebaseConfig = mode === "dev-project" ? developmentConfig : productionConfig

const app = initializeApp(firebaseConfig)

// App Check must initialize before anything that calls a protected backend.
//
// Local dev, the emulator and Playwright have no reCAPTCHA to pass, so they run
// on a debug token instead. Set VITE_APPCHECK_DEBUG_TOKEN to a token registered
// under App Check > Apps > (app) > Manage debug tokens; leaving it unset makes
// the SDK mint one and log it to the console, which is fine for a local browser
// but useless in CI, where the token has to stay stable across runs.
//
// Assigning to self.FIREBASE_APPCHECK_DEBUG_TOKEN only has an effect before
// initializeAppCheck runs, so this ordering matters.
const isDevLike = import.meta.env.DEV || mode === "emulator"
if (isDevLike) {
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN ?? true
}

const recaptchaSiteKey =
  mode === "dev-project" ? RECAPTCHA_SITE_KEYS.development : RECAPTCHA_SITE_KEYS.production

// On a debug token the SDK never asks the provider for a token, but it still
// initializes it, and reCAPTCHA throws "Missing required parameters: sitekey"
// on every page without a key. So a local run against the dev project, which
// has no key yet, gets a provider that loads nothing.
const noSiteKeyProvider = () =>
  new CustomProvider({
    getToken: () => Promise.reject(new Error("No reCAPTCHA site key for this project"))
  })

// try/catch absorbs re-initialization on Vite HMR, same as auth below. A missing
// site key is non-fatal on purpose: App Check is unenforced today, so a dev
// build without a key should still boot rather than take the whole app down.
export const appCheck = (() => {
  if (!recaptchaSiteKey && !isDevLike) return null
  try {
    return initializeAppCheck(app, {
      provider: recaptchaSiteKey ? new ReCaptchaV3Provider(recaptchaSiteKey) : noSiteKeyProvider(),
      isTokenAutoRefreshEnabled: true
    })
  } catch (err) {
    if (import.meta.env.DEV) console.warn("App Check init skipped:", err)
    return null
  }
})()

// Force localStorage persistence so Playwright's storageState can capture the
// auth token; getAuth() defaults to IndexedDB which Playwright cannot capture.
// try/catch absorbs re-initialization on Vite HMR.
export const auth = (() => {
  try {
    return initializeAuth(app, { persistence: browserLocalPersistence })
  } catch {
    return getAuth(app)
  }
})()
export const db = getFirestore(app)

export const functions = getFunctions(app)
if (mode === "emulator") {
  connectFunctionsEmulator(functions, "127.0.0.1", 5301)
}

let analyticsInstance = null
export const getAnalyticsInstance = async () => {
  if (analyticsInstance) return analyticsInstance
  if (await isSupported()) {
    analyticsInstance = getAnalytics(app)
  }
  return analyticsInstance
}

export default app
